/* ============================================================
   AuthModal — Login / Register modal (Figma design)
   ============================================================ */
import { useState } from 'react';
import useRecaptcha from '../hooks/useRecaptcha';
import './AuthModal.css';

const logoColor = '/logo-color.svg';

export default function AuthModal({ isOpen, onClose, onAuth }) {
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { execute: execRecaptcha } = useRecaptcha();

  if (!isOpen) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const recaptchaToken = await execRecaptcha('login');
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, recaptcha_token: recaptchaToken }),
      });
      const data = await res.json();
      if (!res.ok || !data.exito) throw new Error(data.mensaje || 'Credenciales incorrectas');
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.usuario));
      onAuth?.(data.usuario);
      onClose();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPw) { setError('Las contraseñas no coinciden'); return; }
    setLoading(true);
    try {
      const recaptchaToken = await execRecaptcha('register');
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, password_confirmation: confirmPw, recaptcha_token: recaptchaToken }),
      });
      const data = await res.json();
      if (!res.ok || !data.exito) {
        const msg = data.errores
          ? Object.values(data.errores).flat().join('. ')
          : data.mensaje || 'Error al registrar';
        throw new Error(msg);
      }
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.usuario));
      onAuth?.(data.usuario);
      onClose();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail(''); setPassword(''); setName(''); setConfirmPw('');
    setError(''); setShowPw(false);
  };

  const switchTab = (t) => {
    setTab(t); setError('');
  };

  return (
    <div className="auth-overlay" onClick={(e) => { if (e.target === e.currentTarget) { onClose(); resetForm(); } }}>
      <div className="auth-modal">
        <button className="auth-modal__close" onClick={() => { onClose(); resetForm(); }} aria-label="Cerrar">×</button>

        {/* Logo */}
        <div className="auth-modal__logo">
          <img src={logoColor} alt="+eventos" />
        </div>

        {/* Header */}
        <div className="auth-modal__header">
          <h2>Bienvenido a +Eventos</h2>
          <p>Tu conserjería digital de entretenimiento</p>
        </div>

        {/* Tabs */}
        <div className="auth-tabs">
          <button className={`auth-tabs__btn ${tab === 'login' ? 'auth-tabs__btn--active' : ''}`}
            onClick={() => switchTab('login')}>Iniciar Sesión</button>
          <button className={`auth-tabs__btn ${tab === 'register' ? 'auth-tabs__btn--active' : ''}`}
            onClick={() => switchTab('register')}>Crear Cuenta</button>
        </div>

        {/* Login form */}
        {tab === 'login' && (
          <form className="auth-form" onSubmit={handleLogin}>
            {error && <div className="auth-error">{error}</div>}

            <div className="auth-field">
              <label>Correo electrónico</label>
              <div className="auth-field__wrap">
                <span className="auth-field__icon">
                  <svg width="20" height="16" viewBox="0 0 20 16" fill="none"><path d="M18 0H2C.9 0 .01.9.01 2L0 14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2zm0 4-8 5-8-5V2l8 5 8-5v2z" fill="#7a7585"/></svg>
                </span>
                <input type="email" placeholder="ejemplo@correo.co"
                  value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
              </div>
            </div>

            <div className="auth-field">
              <label>Contraseña</label>
              <div className="auth-field__wrap">
                <span className="auth-field__icon">
                  <svg width="16" height="21" viewBox="0 0 16 21" fill="none"><path d="M14 7h-1V5c0-2.76-2.24-5-5-5S3 2.24 3 5v2H2C.9 7 0 7.9 0 9v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H4.9V5c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" fill="#7a7585"/></svg>
                </span>
                <input type={showPw ? 'text' : 'password'} placeholder="••••••••"
                  value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
                <button type="button" className="auth-field__toggle" onClick={() => setShowPw(!showPw)}>
                  <svg width="22" height="15" viewBox="0 0 22 15" fill="none">
                    {showPw ? (
                      <path d="M11 0C6 0 1.73 3.11 0 7.5 1.73 11.89 6 15 11 15s9.27-3.11 11-7.5C20.27 3.11 16 0 11 0zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="#7a7585"/>
                    ) : (
                      <path d="M11 4.5c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16c.57-.23 1.18-.36 1.83-.36zM1 1.27l2.28 2.28.46.46C2.08 5.35.78 7.02 0 9.5c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L18.73 19 20 17.73 2.27 0 1 1.27zM6.53 6.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="#7a7585"/>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div className="auth-forgot">
              <a href="/recuperar">¿Olvidaste tu contraseña?</a>
            </div>

            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? 'Ingresando...' : 'Entrar'}
            </button>
          </form>
        )}

        {/* Register form */}
        {tab === 'register' && (
          <form className="auth-form" onSubmit={handleRegister}>
            {error && <div className="auth-error">{error}</div>}

            <div className="auth-field">
              <label>Nombre completo</label>
              <div className="auth-field__wrap">
                <span className="auth-field__icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 8C6.9 8 5.96 7.61 5.18 6.83C4.39 6.04 4 5.1 4 4s.39-2.04 1.18-2.83C5.96.39 6.9 0 8 0s2.04.39 2.83 1.18C11.61 1.96 12 2.9 12 4s-.39 2.04-1.18 2.83C10.04 7.61 9.1 8 8 8zM0 16v-2.8c0-.57.15-1.09.44-1.56.29-.48.68-.84 1.16-1.09C2.63 10.03 3.68 9.65 4.75 9.39 5.82 9.13 6.9 9 8 9s2.18.13 3.25.39c1.07.26 2.12.64 3.15 1.16.48.25.87.61 1.16 1.09.29.47.44.99.44 1.56V16H0z" fill="#7a7585"/></svg>
                </span>
                <input type="text" placeholder="Tu nombre"
                  value={name} onChange={e => setName(e.target.value)} required autoComplete="name" />
              </div>
            </div>

            <div className="auth-field">
              <label>Correo electrónico</label>
              <div className="auth-field__wrap">
                <span className="auth-field__icon">
                  <svg width="20" height="16" viewBox="0 0 20 16" fill="none"><path d="M18 0H2C.9 0 .01.9.01 2L0 14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2zm0 4-8 5-8-5V2l8 5 8-5v2z" fill="#7a7585"/></svg>
                </span>
                <input type="email" placeholder="ejemplo@correo.co"
                  value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
              </div>
            </div>

            <div className="auth-field">
              <label>Contraseña</label>
              <div className="auth-field__wrap">
                <span className="auth-field__icon">
                  <svg width="16" height="21" viewBox="0 0 16 21" fill="none"><path d="M14 7h-1V5c0-2.76-2.24-5-5-5S3 2.24 3 5v2H2C.9 7 0 7.9 0 9v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H4.9V5c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" fill="#7a7585"/></svg>
                </span>
                <input type={showPw ? 'text' : 'password'} placeholder="Mínimo 8 caracteres"
                  value={password} onChange={e => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
                <button type="button" className="auth-field__toggle" onClick={() => setShowPw(!showPw)}>
                  <svg width="22" height="15" viewBox="0 0 22 15" fill="none">
                    <path d="M11 0C6 0 1.73 3.11 0 7.5 1.73 11.89 6 15 11 15s9.27-3.11 11-7.5C20.27 3.11 16 0 11 0zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="#7a7585"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="auth-field">
              <label>Confirmar contraseña</label>
              <div className="auth-field__wrap">
                <span className="auth-field__icon">
                  <svg width="16" height="21" viewBox="0 0 16 21" fill="none"><path d="M14 7h-1V5c0-2.76-2.24-5-5-5S3 2.24 3 5v2H2C.9 7 0 7.9 0 9v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H4.9V5c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" fill="#7a7585"/></svg>
                </span>
                <input type="password" placeholder="Repite tu contraseña"
                  value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required minLength={8} autoComplete="new-password" />
              </div>
            </div>

            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
            </button>
          </form>
        )}

        {/* Divider */}
        <div className="auth-divider">
          <div className="auth-divider__line" />
          <span className="auth-divider__text">o inicia sesión con</span>
          <div className="auth-divider__line" />
        </div>

        {/* Social buttons */}
        <div className="auth-social">
          <button type="button" className="auth-social__btn" onClick={() => {
            fetch('/api/auth/google/redirect').then(r => r.json()).then(d => { if (d.url) window.location.href = d.url; });
          }}>
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Google
          </button>
          <button type="button" className="auth-social__btn" onClick={() => {
            fetch('/api/auth/facebook/redirect').then(r => r.json()).then(d => { if (d.url) window.location.href = d.url; });
          }}>
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#1877F2" d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24c0 11.98 8.776 21.908 20.25 23.708V30.938h-6.094V24h6.094v-5.288c0-6.014 3.583-9.337 9.065-9.337 2.625 0 5.372.469 5.372.469v5.906h-3.026c-2.981 0-3.911 1.85-3.911 3.75V24h6.656l-1.063 6.938H27.75v16.77C39.224 45.908 48 35.98 48 24z"/></svg>
            Facebook
          </button>
        </div>
      </div>
    </div>
  );
}
